import React, { useEffect, useMemo, useState } from 'react';
import * as expensesApi from '../services/expensesApi';
import { PAYMENT_METHODS, PAYMENT_TYPE_LABELS } from '../constants/expensePayments';
import ExpenseManualPaymentForm from './ExpenseManualPaymentForm';
import styles from './ExpensePaymentMatchModal.module.css';

const TYPE_FILTERS = [
    { value: 'all', label: 'Tous' },
    { value: 'tattoo', label: 'Tattoo' },
    { value: 'deposit', label: 'Acompte' },
    { value: 'commission', label: 'Commission' }
];
const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
});
const MONTH_FORMATTER = new Intl.DateTimeFormat('fr-FR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
});
const MONTH_ONLY_FORMATTER = new Intl.DateTimeFormat('fr-FR', {
    month: 'short',
    timeZone: 'UTC'
});
const amount = value => Number.parseFloat(value) || 0;
const money = value => amount(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const monthDate = period => period ? new Date(Date.UTC(period.year, period.month - 1, 1)) : null;
const monthLabel = period => {
    const value = monthDate(period);
    return value ? MONTH_FORMATTER.format(value) : '';
};
const shortMonthLabel = period => {
    const value = monthDate(period);
    return value ? MONTH_ONLY_FORMATTER.format(value).replace('.', '') : '';
};
const date = value => {
    if (!value) return '-';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? '-' : SHORT_DATE_FORMATTER.format(parsed);
};
// Bank dates arrive as the server's local midnight; read them back in local time.
const localIsoDate = value => {
    const parsed = value ? new Date(value) : null;
    if (!parsed || Number.isNaN(parsed.getTime())) return '';
    const pad = number => String(number).padStart(2, '0');
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
};
const paymentPeriodPatch = (paymentDate, window) => {
    if (!paymentDate || !window) return {};
    return {
        payment_period: paymentDate < window.currentStart ? 'previous' : 'current',
        in_previous_tail: paymentDate >= window.previousTailStart
    };
};

export default function ExpensePaymentMatchModal({ expense, expenses: providedExpenses, onClose, onSaved }) {
    const initialExpenses = Array.isArray(providedExpenses) && providedExpenses.length > 0
        ? providedExpenses
        : expense ? [expense] : [];
    const requestedExpenseKey = initialExpenses.map(item => item.id).join(',');
    const [bankCredits, setBankCredits] = useState(initialExpenses);
    const [groupId, setGroupId] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [baseline, setBaseline] = useState(new Map());
    const [selected, setSelected] = useState(new Set());
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [candidateWindow, setCandidateWindow] = useState(null);
    const [showFullPreviousMonth, setShowFullPreviousMonth] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const [showManualForm, setShowManualForm] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const requestedIds = requestedExpenseKey.split(',').map(Number).filter(Number.isInteger);
        expensesApi.getPaymentCandidates(requestedIds).then(result => {
            if (cancelled) return;
            const rows = result.candidates || [];
            setBankCredits(result.expenses || initialExpenses);
            setGroupId(result.group_id || null);
            setCandidates(rows);
            setCandidateWindow(result.candidate_window || null);
            setBaseline(new Map(rows.map(row => [row.key, { ...row }])));
            setSelected(new Set(rows.filter(row => row.matched_current).map(row => row.key)));
        }).catch(err => {
            if (!cancelled) setError(err.message || 'Erreur de chargement');
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    // The modal is remounted for a new reconciliation selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedExpenseKey]);

    const periodCandidates = useMemo(() => candidates.filter(row => (
        showFullPreviousMonth ||
        row.payment_period !== 'previous' ||
        row.in_previous_tail ||
        row.matched_current ||
        row.payment_date_effective !== baseline.get(row.key)?.payment_date_effective
    )), [baseline, candidates, showFullPreviousMonth]);
    const hiddenPreviousCount = useMemo(() => candidates.filter(row => (
        row.payment_period === 'previous' &&
        !row.in_previous_tail &&
        !row.matched_current &&
        row.payment_date_effective === baseline.get(row.key)?.payment_date_effective
    )).length, [baseline, candidates]);
    const visible = useMemo(() => periodCandidates.filter(row => {
        if (filter !== 'all' && row.type !== filter) return false;
        const term = search.trim().toLowerCase();
        return !term || row.client_effective.toLowerCase().includes(term);
    }), [periodCandidates, filter, search]);
    const typeCounts = useMemo(() => periodCandidates.reduce((counts, row) => {
        counts.all += 1;
        if (Object.prototype.hasOwnProperty.call(counts, row.type)) counts[row.type] += 1;
        return counts;
    }, { all: 0, tattoo: 0, deposit: 0, commission: 0 }), [periodCandidates]);

    const selectedRows = candidates.filter(row => selected.has(row.key));
    const originalTotal = selectedRows.reduce((sum, row) => sum + amount(row.amount_original), 0);
    const effectiveTotal = selectedRows.reduce((sum, row) => sum + amount(row.amount_effective), 0);
    const credit = bankCredits.reduce((sum, item) => sum + amount(item.credit), 0);
    const difference = credit - effectiveTotal;

    const update = (key, patch) => setCandidates(current => current.map(row => row.key === key ? { ...row, ...patch } : row));
    const updatePaymentDate = (row, paymentDate) => update(row.key, {
        payment_date: paymentDate,
        payment_date_effective: paymentDate,
        ...paymentPeriodPatch(paymentDate, candidateWindow)
    });
    const toggle = row => {
        if (row.matched_elsewhere) return;
        setSelected(current => {
            const next = new Set(current);
            next.has(row.key) ? next.delete(row.key) : next.add(row.key);
            return next;
        });
    };

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            if (candidates.some(row => !row.payment_date_effective)) {
                throw new Error('La date de paiement est obligatoire.');
            }
            const matches = selectedRows.map(row => ({
                type: row.type,
                payment_id: row.payment_id,
                reservation_id: row.reservation_id,
                commission_id: row.commission_id,
                manual_payment_id: row.manual_payment_id
            }));
            const draftUpdates = candidates.filter(row => {
                const initial = baseline.get(row.key);
                return initial && (row.amount_effective !== initial.amount_effective ||
                    row.method_effective !== initial.method_effective ||
                    row.client_effective !== initial.client_effective ||
                    row.payment_date_effective !== initial.payment_date_effective);
            }).map(row => ({
                type: row.type,
                payment_id: row.payment_id,
                reservation_id: row.reservation_id,
                commission_id: row.commission_id,
                manual_payment_id: row.manual_payment_id,
                ttc: row.amount_effective,
                moyen: row.method_effective,
                client: row.client_effective,
                payment_date: row.payment_date_effective
            }));
            await expensesApi.savePaymentReconciliation(bankCredits, matches, draftUpdates);
            await onSaved?.();
            onClose();
        } catch (err) {
            setError(err.message || 'Erreur lors de l’enregistrement');
        } finally {
            setSaving(false);
        }
    };

    // Keep unsaved edits: only rows the server added are merged in.
    const addManualPayment = async (values) => {
        const created = await expensesApi.createManualPayment(bankCredits[0].id, values);
        const result = await expensesApi.getPaymentCandidates(bankCredits.map(item => item.id));
        const known = new Set(candidates.map(row => row.key));
        const added = (result.candidates || []).filter(row => !known.has(row.key));
        setShowFullPreviousMonth(current => current || added.some(row => row.key === created.key && row.payment_period === 'previous'));
        if (!added.some(row => row.key === created.key)) {
            setShowManualForm(false);
            setError('Paiement ajouté, mais sa date est hors de la période de ce relevé : il n’est pas sélectionné.');
            return;
        }
        setCandidates(current => [...current, ...added]);
        setBaseline(current => new Map([...current, ...added.map(row => [row.key, { ...row }])]));
        setSelected(current => new Set([...current, created.key]));
        setShowManualForm(false);
    };

    const removeManualPayment = async (row) => {
        if (!window.confirm(`Supprimer le paiement manuel de ${money(row.amount_effective)}€ (${row.client_effective}) ?`)) return;
        setError('');
        try {
            await expensesApi.deleteManualPayment(row.manual_payment_id);
            setCandidates(current => current.filter(item => item.key !== row.key));
            setSelected(current => new Set([...current].filter(key => key !== row.key)));
        } catch (err) {
            setError(err.message || 'Erreur lors de la suppression du paiement');
        }
    };

    const manualDefaults = {
        amount: difference > 0.005 ? difference.toFixed(2) : credit.toFixed(2),
        paymentDate: localIsoDate(bankCredits[0]?.date_operation)
    };

    const dissolveGroup = async () => {
        if (!groupId || !bankCredits[0]) return;
        if (!window.confirm('Dissoudre ce groupe ? Les paiements associés seront libérés, mais les brouillons seront conservés.')) return;
        setSaving(true);
        setError('');
        try {
            await expensesApi.dissolvePaymentGroup(bankCredits[0].id);
            await onSaved?.();
            onClose();
        } catch (err) {
            setError(err.message || 'Erreur lors de la dissolution du groupe');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={event => event.stopPropagation()}>
                <div className={styles.header}>
                    <div>
                        <h3 className={styles.title}>Associer des paiements</h3>
                        <div className={styles.subtitle}>
                            {bankCredits.length > 1
                                ? `${bankCredits.length} crédits · Total ${money(credit)}€`
                                : bankCredits[0]
                                    ? `${date(bankCredits[0].date_operation)} · ${bankCredits[0].description} · ${money(bankCredits[0].credit)}€`
                                    : 'Crédit'}
                        </div>
                    </div>
                    <button className={styles.close} onClick={onClose}>×</button>
                </div>
                {bankCredits.length > 1 && (
                    <div className={styles.creditGroupSummary}>
                        {bankCredits.map(item => (
                            <span key={item.id} className={styles.creditGroupItem}>
                                {date(item.date_operation)} <strong>{money(item.credit)}€</strong>
                            </span>
                        ))}
                        <span className={styles.creditGroupTotal}>Total <strong>{money(credit)}€</strong></span>
                    </div>
                )}
                <div className={styles.toolbar}>
                    <input className={styles.search} value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un client..." />
                    <button type="button" className={styles.addManual} disabled={loading || showManualForm} onClick={() => setShowManualForm(true)}>+ Paiement manuel</button>
                    <div className={styles.typeTabs} role="tablist" aria-label="Filtrer par type de paiement">
                        {TYPE_FILTERS.map(type => (
                            <button
                                key={type.value}
                                type="button"
                                role="tab"
                                aria-selected={filter === type.value}
                                className={`${styles.typeTab} ${filter === type.value ? styles.activeTypeTab : ''}`}
                                onClick={() => setFilter(type.value)}
                            >
                                {type.label}<span className={styles.typeCount}>{typeCounts[type.value]}</span>
                            </button>
                        ))}
                    </div>
                </div>
                {candidateWindow && (
                    <div className={styles.periodBar}>
                        <span>
                            {monthLabel(candidateWindow.current)} + fin {shortMonthLabel(candidateWindow.previous)}&nbsp;
                            ({candidateWindow.previous.tail_start_day}–{candidateWindow.previous.end_day})
                        </span>
                        {hiddenPreviousCount > 0 && (
                            <button
                                type="button"
                                className={styles.periodButton}
                                aria-pressed={showFullPreviousMonth}
                                onClick={() => setShowFullPreviousMonth(current => !current)}
                            >
                                {showFullPreviousMonth
                                    ? `Limiter à la fin de ${shortMonthLabel(candidateWindow.previous)}`
                                    : `Voir tout ${shortMonthLabel(candidateWindow.previous)} (${hiddenPreviousCount})`}
                            </button>
                        )}
                    </div>
                )}
                {showManualForm && (
                    <ExpenseManualPaymentForm
                        defaults={manualDefaults}
                        candidates={candidates}
                        onSubmit={addManualPayment}
                        onCancel={() => setShowManualForm(false)}
                    />
                )}
                <div className={styles.body}>
                    {loading ? <div className={styles.empty}>Chargement...</div> : visible.length === 0 ? <div className={styles.empty}>Aucun paiement pour cette période</div> : (
                        <table className={styles.table}><thead><tr><th></th><th>Type</th><th>Client</th><th>Original</th><th>Ajusté</th><th>Date RDV</th><th>Date paiement</th><th>Moyen</th><th>État</th></tr></thead><tbody>
                            {visible.map(row => {
                                const blocked = row.matched_elsewhere;
                                return <tr key={row.key} className={`${selected.has(row.key) ? styles.selectedRow : ''} ${blocked ? styles.blockedRow : ''}`}>
                                    <td><input type="checkbox" checked={selected.has(row.key)} disabled={blocked} onChange={() => toggle(row)} /></td>
                                    <td>
                                        <span className={styles.typeBadge}>{PAYMENT_TYPE_LABELS[row.type] || row.type}</span>
                                        {row.source === 'manual' && <span className={styles.manualBadge} title={row.note || 'Ajouté manuellement'}>manuel</span>}
                                        {row.payment_period === 'previous' && <span className={styles.previousPeriodBadge}>{shortMonthLabel(candidateWindow?.previous)}</span>}
                                    </td>
                                    <td><input disabled={blocked} className={`${styles.input} ${styles.clientInput}`} value={row.client_effective} onChange={e => update(row.key, { client_effective: e.target.value })} /></td>
                                    <td>{money(row.amount_original)}€</td>
                                    <td><input disabled={blocked} type="number" min="0" step="0.01" className={styles.input} value={row.amount_effective} onChange={e => update(row.key, { amount_effective: e.target.value })} />€{row.amount_draft !== null && <div className={styles.draftValue}>brouillon</div>}</td>
                                    <td>{date(row.rdv_date)}</td>
                                    <td>
                                        <div className={styles.dateEditor}>
                                            <input
                                                disabled={blocked}
                                                type="date"
                                                min="2000-01-01"
                                                max="2100-12-31"
                                                required
                                                className={`${styles.input} ${styles.dateInput}`}
                                                value={row.payment_date_effective || ''}
                                                onChange={event => updatePaymentDate(row, event.target.value)}
                                            />
                                            {row.source !== 'manual' && row.payment_date_effective !== row.payment_date_original && (
                                                <button
                                                    type="button"
                                                    disabled={blocked}
                                                    className={styles.resetDate}
                                                    title={`Date originale : ${date(row.payment_date_original)}`}
                                                    onClick={() => updatePaymentDate(row, row.payment_date_original)}
                                                >↺</button>
                                            )}
                                        </div>
                                        {row.source !== 'manual' && row.payment_date_effective !== row.payment_date_original && (
                                            <div className={styles.dateDraftValue}>brouillon · origine {date(row.payment_date_original)}</div>
                                        )}
                                    </td>
                                    <td><select disabled={blocked} className={styles.select} value={row.method_effective} onChange={e => update(row.key, { method_effective: e.target.value })}>{PAYMENT_METHODS.map(method => <option key={method}>{method}</option>)}</select></td>
                                    <td>
                                        {blocked ? <span className={styles.matchState}>Déjà associé</span> : selected.has(row.key) ? 'Sélectionné' : '-'}
                                        {row.source === 'manual' && !blocked && (
                                            <button type="button" className={styles.deleteManual} title="Supprimer ce paiement manuel" onClick={() => removeManualPayment(row)}>Supprimer</button>
                                        )}
                                    </td>
                                </tr>;
                            })}
                        </tbody></table>
                    )}
                </div>
                <div className={styles.summary}>
                    <div className={styles.summaryRow}><span>Crédit bancaire</span><strong>{money(credit)}€</strong></div>
                    <div className={styles.summaryRow}><span>Montant original sélectionné</span><span>{money(originalTotal)}€</span></div>
                    <div className={styles.summaryRow}><span>Montant ajusté sélectionné</span><span>{money(effectiveTotal)}€</span></div>
                    <div className={styles.summaryRow}><span>Différence</span><span className={Math.abs(difference) < .005 ? styles.exact : styles.difference}>{money(difference)}€ · {Math.abs(difference) < .005 ? 'Correspondance exacte' : 'Écart'}</span></div>
                    {error && <div className={styles.difference}>{error}</div>}
                </div>
                <div className={styles.footer}>
                    <div>{groupId && <button className={`${styles.button} ${styles.dissolve}`} disabled={saving} onClick={dissolveGroup}>Dissoudre le groupe</button>}</div>
                    <div className={styles.footerActions}>
                        <button className={`${styles.button} ${styles.cancel}`} onClick={onClose}>Annuler</button>
                        <button className={`${styles.button} ${styles.save}`} disabled={loading || saving} onClick={save}>{saving ? 'Enregistrement...' : 'Enregistrer le rapprochement'}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
